import React, {useState, useRef} from 'react';
import {Link, useParams} from "react-router-dom";
import DisplayMetaWithImport from "../components/DisplayMetaWithImport.comp";
import {formatDate} from "../../../utils/utils";

import {
    v3Level7DQCode
} from "../../../services/metadata.service";


// name: display on preview top / edit form label
// key: column# of excel sheet (0-based) for "Level7 (GSM)" sheet of DQ ClassARC v8.0
// json_key / data_key: model field used to post/read data
const columns = [
    {name: 'DQCO#', key: 0, json_key: 'dqco_no', data_key: 'dqco_no'},
    {name: 'Code', key: 1, json_key: 'code', data_key: 'code'},
    {name: 'Name', key: 2, json_key: 'full_name', data_key: 'full_name'},
    {name: 'Definition', key: 3, json_key: 'definition', data_key: 'definition'},
    {name: 'Difficulty', key: 4, json_key: 'difficulty', data_key: 'difficulty'},
    {name: 'Metadata Distinguisher', key: 5, json_key: 'metadata_distinguisher', data_key: 'metadata_distinguisher'},
    {name: 'DQC#', key: 15, json_key: 'dqc_no', data_key: 'dqc_no'},
    {name: 'DQM#', key: 16, json_key: 'dqm_no', data_key: 'dqm_no'},
    {name: 'Stakeholder#', key: 17, json_key: 'stakeholder_no', data_key: 'stakeholder_no'},
    {name: 'DQA#', key: 18, json_key: 'dqa_no', data_key: 'dqa_no'},
]

// v8.0 new columns are optional in the edit form (the modal defaults every field to required)
const fieldConfig = {
    difficulty: { required: false },
    metadata_distinguisher: { required: false },
}


const
    tableCols = [
    {
        label: 'DQCO#',
        field: 'dqco_no', width: 150
    },
    {
        label: 'Code',
        field: 'code', width: 150
    },
    {
        label: 'Full Name',
        field: 'full_name', width: 150
    },
    {
        label: 'Definition',
        field: 'definition', sort: 'disabled', width: 150
    },
    {
        label: 'Difficulty',
        field: 'difficulty', width: 120
    },
    {
        label: 'Metadata Distinguisher',
        field: 'metadata_distinguisher', sort: 'disabled', width: 200
    },
    {
        label: 'DQC#',
        field: 'dqc_no', width: 120
    },
    {
        label: 'DQM#',
        field: 'dqm_no', width: 120
    },
    {
        label: 'Stakeholder#',
        field: 'stakeholder_no', width: 120
    },
    {
        label: 'DQA#',
        field: 'dqa_no', width: 120
    },
    {
        label: 'Created',
        field: 'createdAt', width: 150
    },
    {
        label: 'Updated',
        field: 'updatedAt', width: 150
    },
    {
        label: 'Actions',
        field: 'actions', sort: 'disabled', searchable: false
    }
]


export default function Level7DQCodePage() {

    const { url_param_str } = useParams();
    const search_query = url_param_str.split('=').length > 1 ? url_param_str.split('=')[1]  : '';

    // --- Server-side pagination state ---
    const [serverPage, setServerPage] = useState(1);
    const [serverLimit, setServerLimit] = useState(10);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverTotalPages, setServerTotalPages] = useState(1);

    // --- Server-side search state (debounced) ---
    const [serverSearch, setServerSearch] = useState(search_query || '');
    const [debouncedSearch, setDebouncedSearch] = useState(search_query || '');
    const debounceTimer = useRef(null);

    const handleServerSearchChange = (newSearch) => {
        setServerSearch(newSearch);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(newSearch);
            setServerPage(1);
        }, 400);
    };

    const fetchPage = async (page, limit, search) => {
        const result = await v3Level7DQCode.listData(page, limit, search);
        if (result && result.data) {
            const { total, page: pg, limit: lm, total_pages } = result.data;
            const clampedPage = total_pages > 0 && pg > total_pages ? total_pages : pg;
            setServerPage(clampedPage || 1);
            setServerLimit(lm || limit);
            setServerTotal(total || 0);
            setServerTotalPages(total_pages || 1);
        }
        return result;
    };

    const handleServerPageChange = (newPage) => { setServerPage(newPage); };
    const handleServerLimitChange = (newLimit) => { setServerPage(1); setServerLimit(newLimit); };

    const paginatedListAPI = async () => fetchPage(serverPage, serverLimit, debouncedSearch);

    const table_row__additional_rendering = (row) => {
        const create_data = formatDate(row.createdAt) + ',\n ' + (row.createdBy ? row.createdBy : '');
        row.createdAt = create_data;

        const update_data = formatDate(row.updatedAt) + ',\n ' + (row.updatedBy ? row.updatedBy : '');
        row.updatedAt = update_data;

        return row;
    }

    return (
        <DisplayMetaWithImport
            title={'Level7 DQCode'}
            modal_title={'Level7 DQCode - Data Preview'}
            sheet_name={'Level7 (GSM)'}
            columns={columns}
            tableCols={tableCols}
            fieldConfig={fieldConfig}
            chunkSize={2000}
            tableSpRender={table_row__additional_rendering}
            search_query={search_query}
            listAPI={paginatedListAPI}
            postAPI={v3Level7DQCode.postData}
            deleteAPI={v3Level7DQCode.deleteEntity}
            updateAPI={v3Level7DQCode.updateEntity}
            preRemoveAPI={v3Level7DQCode.preRemoveCheck}
            serverSidePagination={true}
            serverPage={serverPage}
            serverLimit={serverLimit}
            serverTotal={serverTotal}
            serverTotalPages={serverTotalPages}
            onServerPageChange={handleServerPageChange}
            onServerLimitChange={handleServerLimitChange}
            serverSideSearch={true}
            serverSearch={serverSearch}
            onServerSearchChange={handleServerSearchChange}
            debouncedSearch={debouncedSearch}
        />
    );
}
